// PDF export for AI Research outputs. Parses the model's markdown-ish output
// (headings on their own line, plus Table of Cases / Table of Legislation /
// Bibliography / Footnotes / Citation Check sections) and lays it out cleanly
// in Times New Roman 12 / 1.5 spacing for print.
import { jsPDF } from "jspdf";

export type ResearchPdfInput = {
  title: string;
  subtitle?: string;
  citationSetName?: string;
  citationStyle?: string; // e.g. "OSCOLA"
  body: string;
};

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 22;
const CONTENT_W = PAGE_W - MARGIN * 2;
const LINE = 6.2;

// Section headings we recognise from the model output. The parser matches
// case-insensitively on lines that are ONLY the heading (with optional colon).
const KNOWN_HEADINGS = [
  "Issue",
  "Rule",
  "Application",
  "Conclusion",
  "Citation",
  "Parties",
  "Procedural history",
  "Material facts",
  "Legal issue(s)",
  "Legal issues",
  "Holding / Ratio decidendi",
  "Ratio decidendi",
  "Reasoning",
  "Order",
  "Significance",
  "Court & coram",
  "Legal principles engaged",
  "Obiter of note",
  "Order / outcome",
  "Significance for practice",
  "Footnotes",
  "Table of Cases",
  "Table of Legislation",
  "Bibliography",
  "Books",
  "Journal articles",
  "Online sources",
  "Citation Check",
];

type Block = { kind: "heading" | "para"; text: string };

function parseBlocks(body: string): Block[] {
  const lines = body.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let buf: string[] = [];
  const flush = () => {
    const t = buf.join(" ").replace(/\s+/g, " ").trim();
    if (t) blocks.push({ kind: "para", text: t });
    buf = [];
  };
  const isHeading = (raw: string): string | null => {
    const line = raw
      .trim()
      .replace(/^#+\s*/, "")
      .replace(/^\*\*(.+)\*\*$/, "$1")
      .replace(/:$/, "")
      .trim();
    if (!line || line.length > 80) return null;
    const norm = line.toLowerCase();
    const match = KNOWN_HEADINGS.find((h) => h.toLowerCase() === norm);
    return match ?? null;
  };
  for (const raw of lines) {
    if (!raw.trim()) {
      flush();
      continue;
    }
    const heading = isHeading(raw);
    if (heading) {
      flush();
      blocks.push({ kind: "heading", text: heading });
      continue;
    }
    buf.push(raw.trim());
  }
  flush();
  return blocks;
}

// Strip common markdown emphasis for rendering plain text in jsPDF.
function stripMd(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1");
}

export function buildResearchPdf(input: ResearchPdfInput): jsPDF {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFont("times", "normal");
  doc.setFontSize(12);
  let y = MARGIN;

  const ensure = (needed: number) => {
    if (y + needed > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };
  const heading = (text: string, size = 13) => {
    ensure(LINE + 4);
    y += 2;
    doc.setFont("times", "bold");
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, CONTENT_W);
    doc.text(lines, MARGIN, y);
    y += lines.length * (LINE - 0.5) + 1;
    doc.setFont("times", "normal");
    doc.setFontSize(12);
  };
  const paragraph = (text: string) => {
    const t = stripMd(text);
    const lines = doc.splitTextToSize(t, CONTENT_W);
    lines.forEach((ln: string) => {
      ensure(LINE);
      doc.text(ln, MARGIN, y);
      y += LINE;
    });
    y += 1.5;
  };

  // ---- Header ----
  doc.setFont("times", "bold");
  doc.setFontSize(17);
  const title = doc.splitTextToSize(input.title || "Research Output", CONTENT_W);
  doc.text(title, PAGE_W / 2, y, { align: "center" });
  y += title.length * 7.5;

  if (input.subtitle) {
    doc.setFont("times", "italic");
    doc.setFontSize(11);
    doc.text(input.subtitle, PAGE_W / 2, y + 1, { align: "center" });
    y += 6;
  }

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  const badge = [
    `Citation style: ${input.citationStyle || "OSCOLA"}`,
    input.citationSetName ? `Saved set: ${input.citationSetName}` : null,
    `Generated ${new Date().toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`,
  ]
    .filter(Boolean)
    .join(" · ");
  doc.text(badge, PAGE_W / 2, y, { align: "center" });
  y += 4;
  doc.setDrawColor(180);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 4;
  doc.setFont("times", "normal");
  doc.setFontSize(12);

  // ---- Body ----
  const blocks = parseBlocks(input.body);
  if (blocks.length === 0) {
    paragraph(input.body || "(empty)");
  } else {
    for (const b of blocks) {
      if (b.kind === "heading") heading(b.text);
      else paragraph(b.text);
    }
  }

  // ---- Footer ----
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("times", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      `${input.title || "Research"} — page ${i} of ${pageCount}`,
      PAGE_W / 2,
      PAGE_H - 8,
      { align: "center" },
    );
    doc.setTextColor(0);
  }
  return doc;
}

export function downloadResearchPdf(input: ResearchPdfInput, filename: string) {
  const doc = buildResearchPdf(input);
  doc.save(filename);
}
